import { formatDateShort } from '../../lib/util/date';

/**
 * Safely format a date string into a short, human-readable format.
 * Returns null for empty input and preserves the input for unparsable dates.
 */
export const safeFormat = (d?: string | null): string | null => {
  if (!d) return null;
  try {
    return formatDateShort(new Date(d)).replace(/\//g, '-');
  } catch {
    return d;
  }
};

/** Number of years a content date may be old and still be displayed. */
const MAX_DISPLAY_AGE_YEARS = 5;

export type DisplayDateType = 'lastReviewed' | 'lastUpdated' | 'pageCreated';

export type DisplayDate = {
  date: string;
  type: DisplayDateType;
};

/**
 * True when `dateStr` is parseable and its calendar year is within the last
 * {@link MAX_DISPLAY_AGE_YEARS} years (inclusive).
 */
export const isWithinDisplayWindow = (dateStr?: string): boolean => {
  const year = new Date(dateStr ?? '').getFullYear();
  return (
    !Number.isNaN(year) &&
    new Date().getFullYear() - year <= MAX_DISPLAY_AGE_YEARS
  );
};

/**
 * Resolves which content date to show on the Learn page, applying precedence
 * (last reviewed → last updated → page created) and only returning a date
 * within the display window. Returns `null` when no eligible date exists.
 */
export const getDisplayDate = ({
  lastReviewed,
  lastUpdated,
  pageCreated,
}: {
  lastReviewed?: string;
  lastUpdated?: string;
  pageCreated?: string;
}): DisplayDate | null => {
  if (lastReviewed && isWithinDisplayWindow(lastReviewed)) {
    return { date: lastReviewed, type: 'lastReviewed' };
  }
  if (lastUpdated && isWithinDisplayWindow(lastUpdated)) {
    return { date: lastUpdated, type: 'lastUpdated' };
  }
  if (pageCreated && isWithinDisplayWindow(pageCreated)) {
    return { date: pageCreated, type: 'pageCreated' };
  }
  return null;
};

const dateUtils = {
  safeFormat,
  isWithinDisplayWindow,
  getDisplayDate,
};

export default dateUtils;
